import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-sidebar.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@chancenlogistik/client/components/logistik-component-product-overview';
import { type LogistikComponentProductOverview } from '@chancenlogistik/client/components/logistik-component-product-overview.js';
import '@chancenlogistik/client/components/logistik-component-sidebar';
import '@chancenlogistik/client/pages/logistik-page-cart';
import '@chancenlogistik/client/pages/logistik-page-checkout';
import '@chancenlogistik/client/pages/logistik-page-landing';
import '@chancenlogistik/client/pages/logistik-page-no-permission';
import '@chancenlogistik/client/pages/logistik-page-order-status';
import '@chancenlogistik/client/pages/logistik-page-order-success';
import '@chancenlogistik/client/pages/logistik-page-product-details';
import {
  LogistikFirstLevelPage,
  LogistikProductCategoryForUrl,
  LogistikRoutes,
  LogistikSecondLevelPage,
} from '@chancenlogistik/client/routes.js';
import '@chancenwerk/client/components/chancen-component-login-redirect.js';
import '@chancenwerk/client/pages/chancen-page-impressum-privacy.js';
import {
  AdvertisingMaterial,
  ChancenBrief,
  EventTicket,
  Furnishing,
  WorkMaterial,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import { AdvertisingMaterialAPI } from '@chancenwerk/db/api/AdvertisingMaterialAPI.js';
import { LearningMaterialAPI } from '@chancenwerk/db/api/LearningMaterialAPI.js';
import { LogistikProductSetAPI } from '@chancenwerk/db/api/LogistikProductSetAPI.js';
import { TravelCertificateAPI } from '@chancenwerk/db/api/TravelCertificateAPI.js';
import {
  LogistikAdvertisingMaterialCategory,
  LogistikLearningMaterialCategory,
  LogistikProductCategory,
  LogistikProductTargetAudience,
  LogistikURLParameters,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-writer/cr-writer-toolbar.js';
import '@chancenwerk/design/components/fonts.js';
import { orderRouter } from '@chancenwerk/logistik/client/pages/logistik-page-order-status.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { watch } from '@lit-labs/preact-signals';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { LogistikComponentProductOrderingEditor } from './components/logistik-component-product-ordering-editor.js';
import { mainScrollContainer } from './globals.js';
import './pages/logistik-page-access.js';
import './pages/logistik-page-entire-trip.js';
import './pages/logistik-page-individual-order.js';
import './pages/logistik-page-inventory.js';
import './pages/logistik-page-options.js';
import './pages/logistik-page-order-evaluation.js';
import './pages/logistik-page-orderings';
import './pages/logistik-page-other-order.js';
import './pages/logistik-page-products.js';
import './pages/logistik-page-terminal.js';

const router = makeRouter(
  {
    root: {
      path: '/',
      render: stateful(() => {
        const user = useObservable(() => currentUser<ChancenwerkUser>(), []);
        if (user) {
          goTo(LogistikRoutes.landing);
          return html`<logistik-page-landing></logistik-page-landing>`;
        }
        return html`<logistik-page-landing></logistik-page-landing>`;
      }),
    },
    [LogistikFirstLevelPage.DASHBOARD]: {
      path: `/${LogistikFirstLevelPage.DASHBOARD}`,
      render: () => html`<logistik-page-dashboard></logistik-page-dashboard>`,
    },
    'order-status': {
      path: '/order/status*',
      render: () => html`<logistik-page-order-status ${css({ height: '100%' })}></logistik-page-order-status>`,
      subRouter: orderRouter,
    },
    auth: {
      path: `/${LogistikFirstLevelPage.AUTH}/${LogistikSecondLevelPage.LOGIN}`,
      render: stateful(() => {
        const user = useObservable(() => currentUser<ChancenwerkUser>(), []);
        if (user) {
          goTo(LogistikRoutes.landing);
          return html`<logistik-page-landing></logistik-page-landing>`;
        }
        return html`<chancen-component-login-redirect></chancen-component-login-redirect>`;
      }),
    },
    cart: {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.CART}`,
      render: () => html`<logistik-page-cart ${css({ height: '100%' })}></logistik-page-cart>`,
    },
    checkout: {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.CHECKOUT}`,
      render: () => html`<logistik-page-checkout ${css({ height: '100%' })}></logistik-page-checkout>`,
    },
    'order-success': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.SUCCESS}`,
      render: () => html`<logistik-page-order-success ${css({ height: '100%' })}></logistik-page-order-success>`,
    },
    'product-details': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.ITEM}/:id`,
      render: ({ id }) =>
        html`<logistik-page-product-details .selectedProductID=${id}></logistik-page-product-details>`,
    },
    'learning-materials': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.LEARNING_MATERIALS}`,
      render: () => html`
        <logistik-component-product-overview
          heading="CHANCENhefte"
          .categories=${['Alles'].concat(Object.values(LogistikLearningMaterialCategory))}
          .categoriesSubsetForFilter=${Object.values(LogistikLearningMaterialCategory)}
          .subscription=${LearningMaterialAPI.subscribePaginated}
          .orderingCategory=${LogistikProductCategory.LEARNING_MATERIAL}
        >
        </logistik-component-product-overview>
      `,
    },
    'product-sets': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.PRODUCT_SETS}`,
      render: () => html`
        <logistik-component-product-overview
          heading="CHANCENsets"
          .subscription=${LogistikProductSetAPI.subscribeForService}
          .orderingCategory=${LogistikProductCategory.SET}
        >
        </logistik-component-product-overview>
      `,
    },
    'advertising-materials': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.ADVERTISING_MATERIALS}`,
      render: () => html`
        <logistik-component-product-overview
          heading="Werbe- und Akquisematerialien"
          .subscription=${AdvertisingMaterialAPI.subscribePaginated}
          .orderingCategory=${LogistikProductCategory.ADVERTISING_MATERIAL}
          .categories=${['Alles'].concat(Object.values(LogistikAdvertisingMaterialCategory))}
          .categoriesSubsetForFilter=${Object.values(LogistikAdvertisingMaterialCategory)}
          .search=${(host: LogistikComponentProductOverview<AdvertisingMaterial>) => html`
            <cr-dropdown-multiselection
              placeholder=${'Zielgruppe'}
              .selectables=${Object.values(LogistikProductTargetAudience)}
              .value=${host.filter.value?.targetAudiences}
              @value-picked=${(e: ValueEvent<[LogistikProductTargetAudience]>) => {
                if (e.detail.value.length)
                  routingState.searchParams.set(LogistikURLParameters.TargetAudiences, e.detail.value.toString());
                else routingState.searchParams.delete(LogistikURLParameters.TargetAudiences);
              }}
            >
            </cr-dropdown-multiselection>
          `}
        ></logistik-component-product-overview>
      `,
    },
    'work-materials': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.WORK_MATERIALS}`,
      render: () => html`
        <logistik-component-product-overview
          heading="Büromaterialien"
          .subscription=${WorkMaterial.subscribePublishedWorkMaterials.bind(WorkMaterial)}
          .orderingCategory=${LogistikProductCategory.WORK_MATERIAL}
        >
        </logistik-component-product-overview>
      `,
    },
    tickets: {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.TICKETS}`,
      render: () => html`
        <logistik-component-product-overview
          heading="Eintrittskarten"
          .subscription=${EventTicket.subscribePublishedEventTickets.bind(EventTicket)}
          .orderingCategory=${LogistikProductCategory.TICKETS}
        >
        </logistik-component-product-overview>
      `,
    },
    furnishing: {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.FURNISHING}`,
      render: () => html`
        <logistik-component-product-overview
          heading="Mobiliar"
          .subscription=${Furnishing.subscribePublishedFurnishings.bind(Furnishing)}
          .orderingCategory=${LogistikProductCategory.FURNISHING}
        >
        </logistik-component-product-overview>
      `,
    },
    'chancen-brief': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.CHANCEN_BRIEF}`,
      render: () => html`
        <logistik-component-product-overview
          heading="CHANCENbriefe"
          .subscription=${ChancenBrief.subscribePublishedChancenBriefe.bind(ChancenBrief)}
          .orderingCategory=${LogistikProductCategory.CHANCEN_BRIEF}
        >
        </logistik-component-product-overview>
      `,
    },
    'travel-certificate': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.TRAVEL_CERTIFICATE}`,
      render: () => html`
        <logistik-component-product-overview
          heading="CHANCENreise-Zertifikate"
          .subscription=${TravelCertificateAPI.subscribePaginated}
          .orderingCategory=${LogistikProductCategory.TRAVEL_CERTIFICATE}
        >
        </logistik-component-product-overview>
      `,
    },
    'workshop-packs': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.WORKSHOP_PACKS}`,
      render: () => html`
        <logistik-component-product-overview
          heading="CHANCENakademie Workshop-Pakete"
          .subscription=${WorkshopPack.subscribePublishedWorkshopPack.bind(WorkshopPack)}
          .orderingCategory=${LogistikProductCategory.WORKSHOP_PACK}
        >
        </logistik-component-product-overview>
      `,
    },
    'individual-order': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.INDIVIDUAL}`,
      render: () => html`<logistik-page-individual-order></logistik-page-individual-order>`,
    },
    'entire-trip': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.ENTIRE_TRIP}`,
      render: () => html`<logistik-page-entire-trip></logistik-page-entire-trip>`,
    },
    'other-order': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.OTHER_ORDER}`,
      render: () => html`<logistik-page-other-order></logistik-page-other-order>`,
    },
    privacy: {
      path: `/${LogistikFirstLevelPage.PRIVACY}`,
      render: () => html`<chancen-page-privacy></chancen-page-privacy>`,
    },
    impress: {
      path: `/${LogistikFirstLevelPage.IMPRESS}`,
      render: () => html`<chancen-page-impressum></chancen-page-impressum>`,
    },
    'admin-terminal': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.TERMINAL}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-terminal></logistik-page-terminal>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-products': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.PRODUCTS}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-products></logistik-page-products>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-inventory': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.INVENTORY}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-inventory></logistik-page-inventory>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-evaluation': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.EVALUATION}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-order-evaluation></logistik-page-order-evaluation>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-configuration': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.CONFIGURATION}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-options></logistik-page-options>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-access': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.ACCESS}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-access></logistik-page-access>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-orderings': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.ORDERINGS}`,
      render: () =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => html`<logistik-page-orderings></logistik-page-orderings>`,
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'admin-orderings-for-type': {
      path: `/${LogistikFirstLevelPage.ADMIN}/${LogistikSecondLevelPage.ORDERINGS}/:productCategory`,
      render: ({ productCategory }) =>
        permissionGuard(
          ChancenPermission.logistik.LOGIN_LOGISTIK,
          () => {
            const categoryKey = Object.keys(LogistikProductCategoryForUrl).find(
              key => LogistikProductCategoryForUrl[key] === productCategory,
            ) as keyof typeof LogistikProductCategory | undefined;
            if (categoryKey === undefined) return html`<logistik-page-not-found></logistik-page-not-found>`;
            const category = LogistikProductCategory[categoryKey];
            return LogistikComponentProductOrderingEditor.render({
              category,
            });
          },
          {
            forbiddenTemplate: html`<logistik-page-no-permission></logistik-page-no-permission>`,
          },
        ),
    },
    'customer-routes': {
      path: `/${LogistikFirstLevelPage.CUSTOMER}/*`,
      render: () =>
        authenticationGuard(
          () => html`<logistik-page-dashboard></logistik-page-dashboard>`,
          html`<chancen-component-login-redirect></chancen-component-login-redirect>`,
        ),
    },
  },
  {
    fallback: () => html`<logistik-page-not-found></logistik-page-not-found>`,
  },
).init();

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  override render() {
    return html`
      <cr-flex horizontal ${css({ height: '100%' })}>
        <logistik-component-sidebar></logistik-component-sidebar>
        <cr-flex flex ${css({ height: '100%', minWidth: '0', backgroundColor: this.colors.neutral.shade(50) })}>
          <cr-flex
            ${css({
              overflow: 'auto',
              height: '100%',
            })}
            ${ref(mainScrollContainer)}
          >
            ${watch(router.outlet)}
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}
